'use client';

import Link from 'next/link';
import { FormEvent, useEffect, useState } from 'react';
import { brandConfig } from '@/config/brand';
import { SiteShell } from '@/platform/layout/SiteShell';
import { LoginLink } from '@/platform/auth/LoginLink';
import {
  CATEGORY_LABELS,
  listFeedback,
  submitFeedback,
  toggleUpvote,
  type FeedbackCategory,
  type FeedbackStatus,
  type Suggestion,
} from './api';
import { StatusBadge } from './StatusBadge';
import { ComposeLegalFooter } from '@/platform/legal/ComposeLegalFooter';
import { policyErrorMessage } from '@/platform/youtube/policyMessages';
import { useComposePolicy } from '@/platform/youtube/useComposePolicy';
import { useFeedbackCapabilities } from './useFeedbackCapabilities';

export interface FeedbackBoardProps {
  isAuthenticated: boolean;
}

export function FeedbackBoard({ isAuthenticated }: FeedbackBoardProps) {
  const capabilities = useFeedbackCapabilities();
  const [items, setItems] = useState<Suggestion[]>([]);
  const [sort, setSort] = useState<'upvotes' | 'recent'>('upvotes');
  const [category, setCategory] = useState<FeedbackCategory | ''>('');
  const [status, setStatus] = useState<FeedbackStatus | ''>('');
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [formCategory, setFormCategory] = useState<FeedbackCategory>('feature');
  const { validateTexts } = useComposePolicy();

  useEffect(() => {
    if (!capabilities.sortUpvotes && sort === 'upvotes') {
      setSort('recent');
    }
  }, [capabilities.sortUpvotes, sort]);

  useEffect(() => {
    const effectiveSort = capabilities.sortUpvotes ? sort : 'recent';
    listFeedback({
      sort: effectiveSort,
      category: category || undefined,
      status: status || undefined,
    })
      .then((res) => setItems(res.suggestions))
      .catch((err: Error) => setError(err.message));
  }, [sort, category, status, capabilities.sortUpvotes]);

  async function handleUpvote(id: string) {
    if (!isAuthenticated || !capabilities.upvotes) return;
    try {
      const res = await toggleUpvote(id);
      setItems((prev) =>
        prev.map((s) =>
          s.id === id ? { ...s, upvoteCount: res.upvoteCount, myUpvoted: res.upvoted } : s,
        ),
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upvote failed');
    }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!isAuthenticated) return;
    const policyErr = validateTexts([title, body]);
    if (policyErr) {
      setError(policyErr);
      return;
    }
    try {
      const sug = await submitFeedback({ title, body, category: formCategory });
      setItems((prev) => [sug, ...prev]);
      setShowForm(false);
      setTitle('');
      setBody('');
      setError(null);
    } catch (err) {
      setError(policyErrorMessage(err) ?? (err instanceof Error ? err.message : 'Submit failed'));
    }
  }

  const loginHint = capabilities.upvotes ? 'Log in to suggest or upvote' : 'Log in to submit feedback';

  return (
    <SiteShell
      title={brandConfig.feedback.label}
      description={brandConfig.feedback.description}
      actions={
        isAuthenticated ? (
          <button type="button" className="btn btn-primary" onClick={() => setShowForm((v) => !v)}>
            {showForm ? 'Cancel' : 'Submit suggestion'}
          </button>
        ) : (
          <p className="text-muted" style={{ margin: 0 }}>
            <LoginLink returnTo="/feedback/">{loginHint}</LoginLink>
          </p>
        )
      }
      toolbar={
        <div className="feedback-board__filters">
          {capabilities.sortUpvotes && (
            <label>
              Sort
              <select value={sort} onChange={(e) => setSort(e.target.value as 'upvotes' | 'recent')}>
                <option value="upvotes">Most upvotes</option>
                <option value="recent">Most recent</option>
              </select>
            </label>
          )}
          <label>
            Category
            <select value={category} onChange={(e) => setCategory(e.target.value as FeedbackCategory | '')}>
              <option value="">All</option>
              {Object.entries(CATEGORY_LABELS).map(([k, label]) => (
                <option key={k} value={k}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <label>
            Status
            <select value={status} onChange={(e) => setStatus(e.target.value as FeedbackStatus | '')}>
              <option value="">All</option>
              <option value="open">Open</option>
              <option value="under_review">Under review</option>
              <option value="planned">Planned</option>
              <option value="shipped">Shipped</option>
              <option value="declined">Declined</option>
            </select>
          </label>
        </div>
      }
      alerts={error ? <p className="form-error">{error}</p> : undefined}
    >
      {showForm && isAuthenticated && (
        <form className="feedback-board__form feed-card" onSubmit={(e) => void handleSubmit(e)}>
          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title" required />
          <select value={formCategory} onChange={(e) => setFormCategory(e.target.value as FeedbackCategory)}>
            {Object.entries(CATEGORY_LABELS).map(([k, label]) => (
              <option key={k} value={k}>
                {label}
              </option>
            ))}
          </select>
          <textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="Describe your idea" required />
          <button type="submit" className="btn btn-primary">
            Submit
          </button>
          <ComposeLegalFooter />
        </form>
      )}

      <ul className="feedback-board__list">
        {items.map((s) => (
          <li key={s.id} className="feedback-board__item">
            {capabilities.upvotes && (
              <div className="feedback-board__votes">
                <button
                  type="button"
                  disabled={!isAuthenticated}
                  aria-pressed={s.myUpvoted}
                  onClick={() => void handleUpvote(s.id)}
                >
                  ▲ {s.upvoteCount}
                </button>
              </div>
            )}
            <div>
              <Link href={`/feedback/${s.id}/`}>
                <strong>{s.title}</strong>
              </Link>
              <div className="feedback-board__meta">
                <StatusBadge status={s.status} />
                <span>{CATEGORY_LABELS[s.category]}</span>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </SiteShell>
  );
}
